import { ComponentProps } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';

import { CloseIcon } from '@/assets/icons';
import { cn } from '@/utils/tailwind';

type DialogProps = ComponentProps<typeof DialogPrimitive.Root>;
type DialogTriggerProps = ComponentProps<typeof DialogPrimitive.Trigger>;
type DialogPortalProps = ComponentProps<typeof DialogPrimitive.Portal>;
type DialogOverlayProps = ComponentProps<typeof DialogPrimitive.Overlay>;
type DialogCloseProps = ComponentProps<typeof DialogPrimitive.Close>;
type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
};

const Dialog = ({ ...props }: DialogProps) => {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
};

const DialogTrigger = ({ ...props }: DialogTriggerProps) => {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
};

const DialogPortal = ({ ...props }: DialogPortalProps) => {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
};

const DialogOverlay = ({ className, ...props }: DialogOverlayProps) => {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-[#57534E4D] backdrop-blur-md',
        className
      )}
      {...props}
    />
  );
};

const DialogClose = ({ ...props }: DialogCloseProps) => {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
};

const DialogContent = ({ className, children, showCloseButton, ...props }: DialogContentProps) => {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'bg-custom-warm-grey-25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-fit max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-[16px] outline outline-custom-warm-grey-200 p-6 shadow-custom-popover',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close className="cursor-pointer absolute top-5 right-5 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5">
            <CloseIcon width={20} height={20} className="text-custom-warm-grey-400" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};

export { Dialog, DialogClose, DialogContent, DialogOverlay, DialogPortal, DialogTrigger };
